import React, { useEffect, useRef, useState } from 'react';
import { cn } from './model/derive';
import FloatingTip from './FloatingTip';
import TdButton from './Button';
import TdSelect from './TdSelect';
import useRowMotion from './useRowMotion';
import {
  ONE_PAGE,
  ONE_PAGE_MAX,
  SLOT_PLACEHOLDER,
  SLOT_TIPS,
  TAG_FIELD,
  slotCheck,
  stripScheme,
} from './model/handoffSlots';
import './motion.css';

/**
 * Submit 모달의 인계 유형별 링크 칸 (1016 1차 수정 §1).
 * 한 줄 = 링크 종류 드롭다운 + (i) 툴팁 · 주소 입력(입력 즉시 검사) + × — 추천 칸을 띄우고 "+ Add link" 로 더한다.
 */

const MAX_ROWS = 10;

const isWide = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(min-width: 768px)').matches;

/** (i) — 넓은 화면은 마우스를 올리거나 포커스하면, 좁은 화면은 눌러서 연다. 바깥을 누르거나 Escape 면 닫힌다 */
const SlotTip = ({ id, rule, label, open, onOpen, onClose }) => {
  const anchorRef = useRef(null);
  return (
    <span data-tip className='relative inline-flex'>
      <button
        ref={anchorRef}
        type='button'
        aria-label={`Accepted links for ${label}`}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onMouseEnter={() => isWide() && onOpen()}
        onMouseLeave={() => isWide() && onClose()}
        onFocus={() => isWide() && onOpen()}
        onBlur={() => isWide() && onClose()}
        onClick={() => !isWide() && (open ? onClose() : onOpen())}
        className='flex h-7 w-7 items-center justify-center rounded-full text-text-secondary hover:bg-grayscale-gray01'
      >
        <span
          aria-hidden
          className='flex h-3.5 w-3.5 items-center justify-center rounded-full border border-current text-[9px] font-medium leading-none'
        >
          i
        </span>
      </button>
      <FloatingTip anchorRef={anchorRef} open={open} id={id}>
        {SLOT_TIPS[rule].map((line) => (
          <span key={line}>{line}</span>
        ))}
      </FloatingTip>
    </span>
  );
};

const defOf = (slots, tag) => slots.find(([k]) => k === tag) ?? slots[0];

/** 처음 띄우는 추천 칸 — 인계 유형의 종류마다 한 줄 */
export const initialSlotRows = (slots) => (slots ?? []).map(([k]) => ({ tag: k, url: '' }));

/**
 * @param slots [[key, label, rule, flag]]
 * @param rows  [{ tag, url }]
 * @param required 꼭 채워야 하는 칸 key (QA: web · repo) — 종류 옆에 Required
 */
export const SlotFields = ({ slots, rows, onChange, disabled, required = [] }) => {
  const [tipKey, setTipKey] = useState(null);
  const motion = useRowMotion(rows, onChange, 16);

  useEffect(() => {
    if (!tipKey) return undefined;
    const onDown = (e) => {
      if (!e.target.closest?.('[data-tip]')) setTipKey(null);
    };
    const onKey = (e) => e.key === 'Escape' && setTipKey(null);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [tipKey]);

  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div className='flex flex-col gap-4'>
      {rows.map((row, i) => {
        const key = motion.keys[i];
        const [tag, label, rule, flag] = defOf(slots, row.tag);
        const error = slotCheck(rule, row.url);
        const inputId = `slot-${key}`;
        return (
          <div
            key={key}
            data-slot-row
            className={cn('flex flex-col gap-2', motion.isFresh(key) && 'td-rise')}
          >
            <div className='flex items-center gap-1'>
              <TdSelect
                aria-label='Link tag'
                value={tag}
                disabled={disabled}
                onChange={(e) => set(i, { tag: e.target.value })}
                className='h-8 w-auto max-w-full rounded-lg border border-green-deep03/20 bg-grayscale-gray00 px-2 text-[13px] font-medium text-text-primary focus:border-green-deep02 focus:outline-none'
              >
                {slots.map(([tk, tl]) => (
                  <option key={tk} value={tk}>
                    {tl}
                  </option>
                ))}
              </TdSelect>
              {required.includes(tag) && (
                <span className='ml-1 text-[12px] font-medium text-functional-failed03'>
                  Required
                </span>
              )}
              <SlotTip
                id={`slot-tip-${key}`}
                rule={rule}
                label={label}
                open={tipKey === key}
                onOpen={() => setTipKey(key)}
                onClose={() => setTipKey((cur) => (cur === key ? null : cur))}
              />
            </div>
            <div className='flex items-center gap-1.5'>
              <input
                id={inputId}
                aria-label={label}
                value={row.url}
                disabled={disabled}
                maxLength={1000}
                placeholder={SLOT_PLACEHOLDER[rule]}
                aria-invalid={!!error}
                aria-describedby={error ? `${inputId}-error` : undefined}
                onChange={(e) => set(i, { url: e.target.value })}
                className={cn(
                  'h-10 min-w-0 flex-1 rounded-lg border bg-grayscale-gray00 px-3 text-[14px] text-text-primary placeholder:text-text-placeholder focus:outline-none',
                  error
                    ? 'border-functional-failed03'
                    : 'border-green-deep03/20 focus:border-green-deep02',
                )}
              />
              <button
                type='button'
                aria-label='Remove link'
                title='Remove link'
                disabled={disabled}
                onClick={(e) => motion.remove(i, e.currentTarget.closest('[data-slot-row]'))}
                className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[18px] text-text-secondary hover:bg-green-deep03/[0.08] hover:text-text-primary'
              >
                ×
              </button>
            </div>
            {error && (
              <p
                id={`${inputId}-error`}
                role='alert'
                className='text-[12px] text-functional-failed03'
              >
                {error}
              </p>
            )}
            {flag === 'repo' && (
              <div
                role='note'
                className='flex items-start gap-2.5 rounded-xl border border-functional-warning02 bg-functional-warning01 px-4 py-3 text-[13px] leading-[150%] text-functional-warning04'
              >
                <span
                  aria-hidden
                  className='mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-functional-warning03'
                />
                <span>
                  Make sure the repository is public so the reviewer can open it, with a README at
                  the root. Start from README_template in Resources.
                </span>
              </div>
            )}
            {flag === 'live' && (
              <p className='text-[12px] leading-[150%] text-text-secondary'>
                Keep this link live after the project ends. It goes in your portfolio.
              </p>
            )}
          </div>
        );
      })}
      <div className='flex flex-col gap-1.5'>
        {rows.length < MAX_ROWS && (
          <TdButton
            variant='ghost'
            size='s'
            disabled={disabled}
            onClick={() => motion.add({ tag: slots[0][0], url: '' })}
            className='w-fit border-dashed border-green-deep03/40 px-3 text-green-deep02 hover:bg-surface-green10 hover:text-green-deep02'
          >
            + Add link
          </TdButton>
        )}
        <span className='text-[12px] text-text-secondary'>
          Remove any link you don’t need, or add more.
        </span>
      </div>
    </div>
  );
};

/** 입력한 칸이 모두 맞는지 — 칸의 규칙은 고른 종류를 따른다 */
export const slotRowsOk = (slots, rows) =>
  rows.every((r) => !r.url.trim() || !slotCheck(defOf(slots, r.tag)[2], r.url));

/** 제출할 슬롯 [{ k, l, field, url }] — 빈 칸은 빼고, 같은 종류가 또 나오면 "Figma file 2" */
export const filledSlots = (slots, rows) => {
  const seen = {};
  return rows
    .filter((r) => r.url.trim())
    .map((r) => {
      const [k, l] = defOf(slots, r.tag);
      seen[k] = (seen[k] ?? 0) + 1;
      return { k, l: seen[k] > 1 ? `${l} ${seen[k]}` : l, field: TAG_FIELD[k], url: r.url.trim() };
    });
};

/** 최종 결과물 — 승인된 팀 링크 (읽기 전용). 1016 2차 수정으로 보류 · 백로그 — 모달에서는 숨겼고 재개 시 쓴다 */
export const ApprovedLinks = ({ rows }) => (
  <div className='flex flex-col gap-2'>
    <span className='text-[13px] font-medium text-text-primary'>Approved team links</span>
    <ul className='divide-y divide-green-deep03/[0.08] rounded-xl border border-green-deep03/[0.12] bg-green-deep03/[0.03]'>
      {rows.map((r) => (
        <li key={r.k} className='flex items-center justify-between gap-3 px-3 py-2.5 text-[13px]'>
          <span className='shrink-0 text-text-secondary'>{r.l}</span>
          {r.url ? (
            <a
              href={r.url}
              target='_blank'
              rel='noreferrer'
              className='min-w-0 truncate text-green-deep03 underline underline-offset-2'
            >
              {stripScheme(r.url)}
            </a>
          ) : (
            <span className='text-text-placeholder'>Not approved yet</span>
          )}
        </li>
      ))}
    </ul>
  </div>
);

/** 최종 결과물 — 한 장 요약 4문항 */
export const OnePageFields = ({ answers, onChange, disabled }) => (
  <div className='flex flex-col gap-4'>
    <span className='text-[15px] font-medium text-text-primary'>One-page summary</span>
    {ONE_PAGE.map((item, i) => (
      <div key={item.q} className='flex flex-col gap-2'>
        <label htmlFor={`op-${i}`} className='text-[13px] font-medium text-text-primary'>
          {item.q}
          <span className='text-functional-failed03'>*</span>
        </label>
        <textarea
          id={`op-${i}`}
          value={answers[i]}
          disabled={disabled}
          maxLength={ONE_PAGE_MAX}
          onChange={(e) =>
            onChange(answers.map((a, j) => (j === i ? e.target.value.slice(0, ONE_PAGE_MAX) : a)))
          }
          className='min-h-[88px] w-full resize-y rounded-lg border border-green-deep03/20 bg-grayscale-gray00 px-3 py-2.5 text-[14px] text-text-primary focus:border-green-deep02 focus:outline-none'
        />
        <span className='flex items-start justify-between gap-3 text-[12px] text-text-secondary'>
          <span>{item.hint}</span>
          <span className='shrink-0 tabular-nums'>
            {answers[i].length.toLocaleString('en-US')} / 1,500
          </span>
        </span>
      </div>
    ))}
  </div>
);
